import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fetchSchoolEmailAlias } from "../../api/schoolEmails";
import { trackEvent } from "../../analytics";
import { ANALYTICS } from "../../lib/analyticsEvents";
import { SCHOOL_SETUP_FLOW_TEXT } from "../../lib/schoolSetupChat";
import CopyAddressButton from "../CopyAddressButton/CopyAddressButton";
import "../CopyAddressButton/CopyAddressButton.css";
import "./SchoolEmailSetupCard.css";

/**
 * User's KiTa forwarding address — compact reference on Mehr.
 */
export default function SchoolEmailSetupCard() {
  const { user } = useAuth();
  const aliasViewTracked = useRef(false);
  const [alias, setAlias] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await fetchSchoolEmailAlias();
      setAlias(data);
    } catch (err) {
      setAlias(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  useEffect(() => {
    if (!alias?.address || aliasViewTracked.current) return;
    aliasViewTracked.current = true;
    trackEvent(ANALYTICS.school_alias_viewed, { surface: "mehr" });
  }, [alias?.address]);

  if (!user) return null;

  const addressRow = loading ? (
    <p className="school-email-setup-muted">Adresse wird geladen…</p>
  ) : alias?.address ? (
    <div className="school-email-setup-row">
      <code className="school-email-setup-address">{alias.address}</code>
      <CopyAddressButton
        address={alias.address}
        onCopied={() => trackEvent(ANALYTICS.school_alias_copied, { surface: "mehr" })}
      />
    </div>
  ) : null;

  return (
    <section className="school-email-setup" aria-labelledby="school-email-setup-title">
      <h3 id="school-email-setup-title" className="school-email-setup-title">
        KiTa-Weiterleitung
      </h3>
      <p className="school-email-setup-hint">
        {SCHOOL_SETUP_FLOW_TEXT.intro}
      </p>

      {error ? (
        <>
          <p className="school-email-setup-error" role="alert">
            {error}
          </p>
          <button
            type="button"
            className="school-email-setup-btn school-email-setup-btn--secondary"
            onClick={load}
            disabled={loading}
          >
            Erneut laden
          </button>
        </>
      ) : (
        addressRow
      )}

      {!loading && alias && !alias.address && !error ? (
        <p className="school-email-setup-error">
          Weiterleitung ist noch nicht konfiguriert.
        </p>
      ) : null}

      <div className="school-email-setup-footer">
        <Link to="/?schoolSetup=1" className="school-email-setup-link">
          Weiterleitung im Chat
        </Link>
        <Link to="/?kids=1" className="school-email-setup-link">
          Kinder und Betreuungstage
        </Link>
      </div>
      <details className="school-email-setup-gmail">
        <summary>So geht&apos;s in Gmail</summary>
        <p>{SCHOOL_SETUP_FLOW_TEXT.gmailHint}</p>
      </details>
    </section>
  );
}
